import {
    Warehouse,
    Truck,
    Boxes,
    Car,
    MapPin,
    Shield,
    Gauge,
    FileCheck,
    Snowflake,
} from "lucide-react";

export const services = [
    {
        slug: "3pl-logistics",
        icon: Boxes,
        title: "3PL Logistics",
        tagline: "End-to-end supply chain management",
        summary:
            "A single accountable partner for inbound, storage, value-add and outbound — engineered for scale across 20+ cities and 100+ client relationships.",
        description:
            "Beeta's 3PL program is built on the operating principles laid down in 2015 — quality, safety, security, flexibility and innovation. We absorb the operational complexity of your supply chain so your team can focus on growth.",
        highlights: [
            "Inbound consolidation",
            "Storage & stock management",
            "Picking, packing & reworking",
            "Outbound distribution",
            "Consulting & process design",
            "Control-tower reporting & KPIs",
        ],
        image:
            "https://images.unsplash.com/photo-1592838064575-70ed626d3a0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3MlMjB0cnVja3xlbnwwfHx8fDE3NzY0MTAwMTV8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        slug: "warehousing",
        icon: Warehouse,
        title: "Warehousing",
        tagline: "168,000 sqft across India",
        summary:
            "Warehouses in Manesar, Faridabad, Greater Noida, Chennai and Pune — all WMS-driven.",
        description:
            "Beeta operates six fully managed warehouses across India's most active industrial belts. From inbound dock to outbound gate, every SKU is tracked under a single Warehouse Management System with cycle-counting and audit trails.",
        highlights: [
            "Inbound receiving & putaway",
            "Storage with stock-control",
            "Picking, packing & reworking",
            "Outbound dispatch",
            "Value-added services",
            "WMS-driven visibility",
        ],
        image:
            "https://images.pexels.com/photos/221047/pexels-photo-221047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
        slug: "transporting",
        icon: Truck,
        title: "Transporting",
        tagline: "10,000+ operations · pan-India distribution",
        summary:
            "Inbound milk-runs, outbound distribution, cross-docks, express, reverse — all under one operator.",
        description:
            "Our transport practice is built around the rhythms of India's manufacturing belts. We run inbound milk-runs for OEMs, outbound distribution for D2C brands, dedicated and shared fleets, cross-docks, express deliveries and reverse-logistics flows — on the same network.",
        highlights: [
            "Inbound collection & milk-run",
            "Fleet & container management",
            "Outbound distribution",
            "Cross-dock operations",
            "Express deliveries",
            "Reverse logistics",
            "Dedicated & shared distribution",
            "In-plant logistics / JIT",
        ],
        image:
            "https://images.unsplash.com/photo-1595587637401-83ff822bd63e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXBwaW5nfGVufDB8fHx8MTc3NjQxMDAxNXww&ixlib=rb-4.1.0&q=85",
    },
    {
        slug: "auto-industry",
        icon: Car,
        title: "3PL — Auto Industry",
        tagline: "Inbound, in-plant & reverse for OEMs",
        summary:
            "Lead Logistics Provider (LLP) programs spanning IBL, in-plant operations and returnable management.",
        description:
            "IBL is a confluence of physical and IT networks built on a Network Design with clear logistics protocols. Beeta runs as the Lead Logistics Provider (LLP) for OEM customers — from Traditional inbound, through VMI/CMI, to Just-in-Time/Just-in-Sequence (JIT/JIS).",
        highlights: [
            "Inbound (Traditional → VMI/CMI → JIT/JIS)",
            "Kitting & light assembly",
            "De-boxing & line feeding",
            "Raw material in-plant store",
            "Finished-goods yard / warehouse",
            "Packaging optimisation",
            "Returnable bin design & management",
        ],
        image:
            "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    },
    {
        slug: "cold-chain",
        icon: Snowflake,
        title: "Cold Chain Supply Chain",
        tagline: "Tech-driven, temperature-controlled transit",
        summary:
            "Newly launched reefer network for food, retail and healthcare — engineered to close the gap between promise and cold-storage reality.",
        description:
            "Beeta has formally entered the Cold Chain Supply Chain network. Building on our 3PL and warehousing DNA, we now run tech-driven, temperature-controlled transit for perishables and life-sciences — with continuous data logging from origin to consignee. One accountable operator, unbroken cold, verifiable proof.",
        highlights: [
            "Multi-temperature reefer fleet (frozen, chilled, ambient)",
            "IoT temperature & humidity data-loggers on every leg",
            "GDP-aligned SOPs for pharma & healthcare shipments",
            "Cold-storage warehousing and cross-docking",
            "Last-mile perishables for food & retail brands",
            "Real-time excursion alerts to the control tower",
            "Digital PoD with time-temperature audit trail",
        ],
        image:
            "/assets/cold-chain.webp",
    },
];

export const stats = [
    { value: "100", suffix: "+", label: "Happy Clients" },
    { value: "10,000", suffix: "+", label: "Operations" },
    { value: "10", suffix: "+", label: "Years of Operation" },
    { value: "90K", suffix: "+", label: "Warehouse sqft" },
];

export const companyFacts = {
    name: "Beeta Logistics Private Limited",
    incorporated: "2015",
    founder: "Mr. Chirag Shridhar",
    founderTitle: "Managing Director",
    headOffice:
        "1816, Tower-4, DLF Corporate Greens, Sector 74-A, Gurgaon 122004, Haryana, India",
    headOfficeShort: "Sector 74-A, Gurgaon",
    gstin: "06AAGCB5268A1ZZ",
    email: "info@beetalogistics.com",
    phone: "+91 99580 18358",
    phoneIntl: "+91 9958018358",
    turnover: "INR 31 Cr approx (FY 2025–26)",
    growthTarget: "25% YoY · next 5 years",
    manpower: { permanent: "45+", contractual: "130+" },
    fleet: { owned: "45", vendor: "100" },
    warehouseSpace: "168,000+ sq.ft across 7+ locations",
    customers: "100+ leading OEMs, Tier-1s & industrial brands",
};

export const testimonials = [
    {
        quote:
            "Beeta has been our logistics partner for over three years and their inbound discipline is the reason our line has not starved once. Two consecutive 'Best Logistics Partner' awards from our team speak for themselves.",
        author: "Supply Chain Head",
        company: "Carrier",
        logo: "/assets/clients/carrier.webp",
        result: "0 line-stoppages · 2 years running",
    },
    {
        quote:
            "From a single lane pilot to a full inbound programme — Beeta absorbed complexity we had struggled with for years. Their control-tower reporting is honest and their operations team is reachable when it matters.",
        author: "General Manager – Logistics",
        company: "Hitachi Astemo",
        logo: "/assets/clients/hitachi.webp",
        result: "Milk-run programme · pan-NCR",
    },
    {
        quote:
            "Reliable, transparent and hands-on. Beeta's team understands that automotive logistics is a promise, not a service line. Their ePOD flow closes our invoicing loop the same day.",
        author: "Head – Materials",
        company: "Rockman Industries",
        logo: "/assets/clients/rockman.webp",
        result: "Same-day ePOD · Letter of Appreciation",
    },
    {
        quote:
            "Beeta was one of the very few 3PLs willing to run a milk-run for our Tier-1 network at the SLA we needed. Ten thousand plus operations in, they are still on-time.",
        author: "Operations Lead",
        company: "Yamaha",
        logo: "/assets/clients/yamaha.webp",
        result: "10,000+ successful operations",
    },
    {
        quote:
            "Their warehousing and value-add teams operate to OEM discipline. Inventory accuracy stays north of 99.9% and cycle counts reconcile without drama every month.",
        author: "Plant Logistics Manager",
        company: "Denso",
        logo: "/assets/clients/denso.webp",
        result: "99.9%+ inventory accuracy",
    },
    {
        quote:
            "Beeta stepped up during our peak-season ramp with additional lanes and manpower at very short notice. Zero missed dispatches through the entire festive quarter.",
        author: "Head – Outbound Logistics",
        company: "Cooper Standard",
        logo: "/assets/clients/cooperstandard.webp",
        result: "0 missed dispatches · peak season",
    },
];

export const warehouses = [
    {
        city: "Manesar — Nawada",
        address:
            "Beeta Logistics Warehouse, Village Nawada, Post Office Sikanderpur, Near Uno Minda, Gurugram, Haryana",
    },
    {
        city: "Bawal",
        address:
            "Beeta Logistics Branch, Plot No. 11, Green Market Naichana Road, Bawal Distt., Rewari, Haryana",
    },
    {
        city: "Faridabad",
        address:
            "Office No. 1250/6, Parwatiya Colony, Sector 22, Faridabad, Haryana 121004",
    },
    {
        city: "Greater Noida",
        address:
            "Beeta Logistics Warehouse, Village Habipur, Near Chouganpur, Greater Noida, Distt. G.B Nagar 201306, UP",
    },
    {
        city: "Chennai — Sriperumbudur",
        address:
            "Beeta Logistics Warehouse, Survey No. 678, Echoor Village, Sriperumbudur Taluk, Kanchipuram Distt, Tamil Nadu 602105",
    },
    {
        city: "Pune — Chakan",
        address:
            "Beeta Logistics Warehouse, Plot No. 4, MIDC, Dagadu Patil Nagar, Thergaon, Near Chakan, Pune",
    },
    {
        city: "Guwahati",
        address:
            "Beeta Logistics Warehouse & Branch, Guwahati, Assam — gateway to all 7 North East states",
    },
];

export const presence = {
    North: ["Gurgaon", "Noida", "Faridabad", "Bawal", "Dharuhera", "Manesar", "Delhi", "Chandigarh", "Ludhiana", "Mohali"],
    South: ["Sriperumbudur", "Chennai", "Sricity", "Bengaluru", "Narasapura", "Hosur"],
    West:  ["Mumbai", "Thane", "Chakan", "Pune", "Nashik"],
    "North East": ["Guwahati", "Shillong", "Itanagar", "Kohima", "Imphal", "Aizawl", "Agartala"],
    Upcoming: ["Becharaji", "Vithalapur", "Ahmedabad"],
};

export const branches = [
    ...new Set([
        "Gurgaon", "Noida", "Faridabad", "Bawal", "Dharuhera", "Manesar",
        "Delhi", "Chandigarh", "Ludhiana", "Mohali",
        "Sriperumbudur", "Chennai", "Sricity", "Bengaluru", "Narasapura", "Hosur",
        "Mumbai", "Thane", "Chakan", "Pune", "Nashik",
        "Guwahati", "Shillong", "Itanagar", "Kohima", "Imphal", "Aizawl", "Agartala",
    ]),
];

export const awards = [
    {
        year: "2017",
        title: "25 Fastest Growing Logistics Companies in India",
        by: "CEO Magazine",
        tone: "recognition",
        image: null, // Upload to /assets/awards/ceo-magazine-2017.webp
    },
    {
        year: "2019–20",
        title: "Best Vendor Appreciation",
        by: "Sekisui DLJM Molding Pvt. Ltd.",
        tone: "customer",
        image: null,
    },
    {
        year: "2023",
        title: "Best Logistics Partner",
        by: "Carrier Airconditioning India Pvt. Ltd.",
        tone: "customer",
        image: null,
    },
    {
        year: "2024",
        title: "Best Logistics Partner (2nd consecutive year)",
        by: "Carrier Airconditioning India Pvt. Ltd.",
        tone: "customer",
        image: null,
    },
    {
        year: "2025",
        title: "Letter of Appreciation",
        by: "Sakura Autoparts India Pvt. Ltd.",
        tone: "customer",
        image: null,
    },
];

export const milestones = [
    { year: "2015", label: "Company incorporated in Gurgaon by Mr. Chirag Shridhar" },
    { year: "2017", label: "Named among India's 25 fastest-growing logistics firms" },
    { year: "2019", label: "Best Vendor recognition · Sekisui DLJM" },
    { year: "2023", label: "Best Logistics Partner · Carrier Airconditioning" },
    { year: "2024", label: "Repeated Best Logistics Partner · Carrier" },
    { year: "2025", label: "Appreciation received from Sakura Autoparts" },
];

export const features = [
    {
        icon: MapPin,
        title: "No Missed Pickups",
        body: "Pickup calls are routed to the right driver automatically — with fall-back escalation if any vehicle misses its window.",
    },
    {
        icon: Gauge,
        title: "Live GPS Tracking",
        body: "Every vehicle streams its position to our control tower. Customers get a branded tracking link on dispatch.",
    },
    {
        icon: Shield,
        title: "Save Cost",
        body: "Live tracking enables better route planning, fewer empty runs and disciplined fuel management — driving down total freight cost.",
    },
    {
        icon: FileCheck,
        title: "ePOD",
        body: "Delivery signatures are captured on the driver app at the delivery point and pushed into your ERP — same day.",
    },
];

export const trackingMock = {
    active: {
        id: "BEETA",
        origin: "Gurugram, HR",
        destination: "Pune, MH",
        mode: "FTL — 32ft Closed Body",
        weight: "8,400 kg",
        etaHuman: "Tomorrow, 4:30 PM",
        status: "In Transit",
        stages: [
            {
                label: "Order Booked",
                location: "Gurugram Hub",
                time: "Mon · 08:12",
                done: true,
            },
            {
                label: "Picked Up",
                location: "Manesar Plant",
                time: "Mon · 14:40",
                done: true,
            },
            {
                label: "Hub Transit",
                location: "Bhopal Scan Centre",
                time: "Tue · 06:20",
                done: true,
            },
            {
                label: "In Transit",
                location: "NH-44 near Jalgaon",
                time: "Now",
                done: true,
                current: true,
            },
            {
                label: "Out for Delivery",
                location: "Pune Spoke",
                time: "Wed · ~10:00",
                done: false,
            },
            {
                label: "Delivered",
                location: "Consignee dock",
                time: "Wed · ~16:30",
                done: false,
            },
        ],
    },
};

export const blogPosts = [
    {
        slug: "cold-chain-launch",
        title: "Beeta enters the Cold Chain Supply Chain network",
        excerpt:
            "Tech-driven, temperature-controlled transit is now live at Beeta — plugging a vital gap for our clients in food, retail and healthcare. Here's what we've built and why it matters.",
        date: "Feb 15, 2026",
        readTime: "4 min read",
        category: "Announcement",
        cover:
            "/assets/cold-chain.webp",
        body: [
            { type: "p", text: "As India's logistics ecosystem rapidly transforms, staying ahead means constantly engineering new solutions for our clients. Today we are proud to share that Beeta Logistics has formally entered the Cold Chain Supply Chain network." },
            { type: "p", text: "Our mission has always been about providing \"Transportation Beyond Compare.\" By adding tech-driven, temperature-controlled transit to our existing 3PL and warehousing capabilities, we are filling a vital gap for our clients in the food, retail and healthcare sectors." },
            { type: "h2", text: "Why cold chain, and why now" },
            { type: "p", text: "Over the past few years our customers have consistently asked the same question: \"You already run our ambient network — can you own the cold links too, end-to-end, on the same SLAs?\" The answer today is yes. Fragmented handoffs between an ambient operator, a reefer operator and a cold-storage warehouse are where excursions happen. One accountable operator means one temperature curve, one PoD, one number to call." },
            { type: "h2", text: "What we've stood up" },
            { type: "ul", items: [
                "A multi-temperature reefer fleet spanning frozen, chilled and controlled-ambient bands",
                "IoT temperature and humidity data-loggers on every leg — streamed to our control tower",
                "GDP-aligned SOPs for pharma and healthcare shipments, from pick-face to consignee",
                "Cold-storage warehousing and cross-docking in our existing industrial hubs",
                "Real-time excursion alerts with escalation to on-call operations",
                "Digital PoD with a time-temperature audit trail, delivered same-day into your ERP",
            ]},
            { type: "h2", text: "Who benefits" },
            { type: "p", text: "Three customer groups will feel the impact first: food and quick-commerce brands protecting shelf life across metros; retail chains harmonising perishables with their existing ambient flow; and healthcare / life-sciences shippers who need GDP-compliant transit with proof, not promises." },
            { type: "h2", text: "The operating principle stays the same" },
            { type: "p", text: "Quality, safety, security, flexibility, innovation — the five principles we started with in 2015. Cold chain isn't a new business for Beeta; it's the same operating model, applied to a colder network. We are excited to support our clients' growth with unmatched operational excellence." },
            { type: "p", text: "If you'd like to explore a lane or a warehouse move under this new capability, our operations team is ready to run the numbers with you." },
        ],
    },
    {
        slug: "reducing-line-side-risk",
        title: "Reducing line-side risk in auto inbound logistics",
        excerpt:
            "A 6-month teardown of what it actually takes to keep an OEM production line from starving — and the three metrics that matter.",
        date: "Jan 12, 2026",
        readTime: "6 min read",
        category: "Auto 3PL",
        cover:
            "https://images.unsplash.com/photo-1565043589221-1a6fd9ae45c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    },
    {
        slug: "warehouse-automation-indian-sme",
        title: "Warehouse automation for the Indian SME — where to actually start",
        excerpt:
            "Most automation projects fail because they skip the boring parts. Here's the sequence we use to move customers from paper to a live WMS in 45 days.",
        date: "Dec 28, 2025",
        readTime: "9 min read",
        category: "Warehousing",
        cover:
            "https://images.pexels.com/photos/221047/pexels-photo-221047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    {
        slug: "route-planning-fuel-savings",
        title: "How smart route planning cut fuel spend by 11% across our fleet",
        excerpt:
            "We instrumented 40 trucks, piloted a new TMS routing engine, and tracked the impact for a full quarter. The results — and the surprises.",
        date: "Dec 04, 2025",
        readTime: "7 min read",
        category: "Transport",
        cover:
            "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    },
    {
        slug: "epod-standardisation",
        title: "Why ePOD is non-negotiable in B2B freight — a standardisation guide",
        excerpt:
            "Paper PODs cost the average B2B shipper 4–6 days of working capital. Here is the operating model that replaces them.",
        date: "Nov 18, 2025",
        readTime: "5 min read",
        category: "Technology",
        cover:
            "/assets/news-epod.webp",
    },
];

// Real Beeta customer roster — sourced from company profile.
// Logo tiles below use PNG crops of the official Beeta customer poster
// (see /public/assets/clients/*.png) so the exact visual identity used by
// each customer is preserved.
export const majorCustomers = [
    {
        category: "Automotive OEMs",
        icon: "car",
        clients: [
            { name: "Yamaha",         src: "/assets/clients/yamaha.webp" },
            { name: "Honda",          src: "/assets/clients/honda.webp" },
            { name: "Royal Enfield",  src: "/assets/clients/royalenfield.webp" },
            { name: "Tata Motors",    src: "/assets/clients/tata.webp" },
            { name: "Mahindra",       src: "/assets/clients/mahindra.webp" },
        ],
    },
    {
        category: "Automotive Components & Tier-1 Suppliers",
        icon: "cog",
        clients: [
            { name: "Denso",             src: "/assets/clients/denso.webp" },
            { name: "Hitachi Astemo",    src: "/assets/clients/hitachi.webp" },
            { name: "KYB",               src: "/assets/clients/kyb.webp" },
            { name: "Tsubaki",           src: "/assets/clients/tsubaki.webp" },
            { name: "D.I.D",             src: "/assets/clients/did.webp" },
            { name: "Thai Summit Group", src: "/assets/clients/thaisummit.webp" },
            { name: "Napino",            src: "/assets/clients/napino.webp" },
            { name: "Rockman",           src: "/assets/clients/rockman.webp" },
            { name: "Ramkrishna Forgings", src: "/assets/clients/ramkrishna.webp" },
            { name: "NSV",               src: "/assets/clients/nsv.webp" },
            { name: "Sentec",            src: "/assets/clients/sentec.webp" },
            { name: "Vaccess",           src: "/assets/clients/vaccess.webp" },
            { name: "Hyco Enterprises",  src: "/assets/clients/hyco.webp" },
            { name: "We Keep It Moving", src: "/assets/clients/wekeepitmoving.webp" },
            { name: "Trontek",           src: "/assets/clients/trontek.webp" },
            { name: "Cooper Standard",   src: "/assets/clients/cooperstandard.webp" },
            { name: "SRS Diecasting",    src: "/assets/clients/srsdiecasting.webp" },
            { name: "JSG",               src: "/assets/clients/jsg.webp" },
            { name: "Elofic",            src: "/assets/clients/elofic.webp" },
            { name: "Stork Group",       src: "/assets/clients/storkgroup.webp" },
            { name: "Wings",             src: "/assets/clients/wings.webp" },
            { name: "Wanfeng Auto",      src: "/assets/clients/wanfengauto.webp" },
            { name: "Sellowrap",         src: "/assets/clients/sellowrap.webp" },
            { name: "Bhatia Alloy",      src: "/assets/clients/bhatiaalloy.webp" },
            { name: "AL Coldforge",      src: "/assets/clients/alcoldforge.webp" },
            { name: "Pranav Vikas",      src: "/assets/clients/pranavvikas.webp" },
        ],
    },
    {
        category: "Industrial & Energy",
        icon: "factory",
        clients: [
            { name: "ENEOS Motor Oil", src: "/assets/clients/eneos.webp" },
            { name: "Carrier",         src: "/assets/clients/carrier.webp" },
        ],
    },
    {
        category: "E-commerce & Quick Commerce",
        icon: "cart",
        clients: [{ name: "Zepto", src: "/assets/clients/zepto.webp" }],
    },
];

export const clientLogos = [
    { name: "Yamaha",              src: "/assets/clients/yamaha.webp" },
    { name: "Honda",               src: "/assets/clients/honda.webp" },
    { name: "Royal Enfield",       src: "/assets/clients/royalenfield.webp" },
    { name: "Tata Motors",         src: "/assets/clients/tata.webp" },
    { name: "Mahindra",            src: "/assets/clients/mahindra.webp" },
    { name: "Denso",               src: "/assets/clients/denso.webp" },
    { name: "Hitachi Astemo",      src: "/assets/clients/hitachi.webp" },
    { name: "KYB",                 src: "/assets/clients/kyb.webp" },
    { name: "Tsubaki",             src: "/assets/clients/tsubaki.webp" },
    { name: "D.I.D",               src: "/assets/clients/did.webp" },
    { name: "Thai Summit",         src: "/assets/clients/thaisummit.webp" },
    { name: "Napino",              src: "/assets/clients/napino.webp" },
    { name: "Rockman",             src: "/assets/clients/rockman.webp" },
    { name: "Ramkrishna Forgings", src: "/assets/clients/ramkrishna.webp" },
    { name: "Cooper Standard",     src: "/assets/clients/cooperstandard.webp" },
    { name: "Trontek",             src: "/assets/clients/trontek.webp" },
    { name: "Elofic",              src: "/assets/clients/elofic.webp" },
    { name: "Stork Group",         src: "/assets/clients/storkgroup.webp" },
    { name: "ENEOS",               src: "/assets/clients/eneos.webp" },
    { name: "Carrier",             src: "/assets/clients/carrier.webp" },
    { name: "Zepto",               src: "/assets/clients/zepto.webp" },
];
